import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Sparkles, Clock, Zap, CreditCard, Rocket, Users,
  MonitorSmartphone, Trophy,
} from "lucide-react";
import { getRegistrationStatus } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";

// Mature cobalt/ink palette — same tokens the rest of / uses.
const C = {
  ink: "#0A0A0A",
  brand: "#1A2DD7",
  brandInk: "#050B5C",
  bone: "#F4F4EE",
};

// Cinematic hero photograph — same "builders at work" theme, wider crop.
const HERO_IMG =
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2400&q=85";

const HERO_STATS = [
  { icon: Clock,              eyebrow: "When",     get: (sc) => sc.lab_date_primary_label },
  { icon: Zap,                eyebrow: "Live",     get: (sc) => `${sc.lab_duration_label} online` },
  { icon: Sparkles,           eyebrow: "Level",    get: () => "Beginner-friendly" },
  { icon: MonitorSmartphone,  eyebrow: "Build",    get: () => "Websites & apps" },
  { icon: CreditCard,         eyebrow: "Payment",  get: (sc) => sc.lab_price_label },
];

export default function Hero() {
  const [cap, setCap] = useState({ can_register: true, cta_label: "Save My Seat", message: "" });
  const sc = useSiteContent();

  useEffect(() => {
    getRegistrationStatus().then(setCap).catch(() => {});
  }, []);

  const primaryLabel = cap.can_register ? `Save My Seat — ${sc.lab_price_label}` : cap.cta_label;
  const targetHref = cap.can_register ? "#register" : "#offer";

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden isolate min-h-[92vh] lg:min-h-[100vh] flex flex-col"
    >
      {/* --- FULL-WIDTH PHOTOGRAPH --- */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Non-technical founder building with AI in a live session"
          className="w-full h-full object-cover object-[62%_center]"
          fetchPriority="high"
          data-testid="hero-bg-image"
        />
      </div>

      {/* --- INK gradient overlay (dark left → clear right) --- */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: `linear-gradient(90deg, ${C.ink} 0%, ${C.ink}F0 22%, ${C.ink}B0 45%, ${C.ink}55 68%, transparent 100%)`,
        }}
      />
      {/* Mobile darker overlay */}
      <div
        className="absolute inset-0 lg:hidden -z-10"
        style={{
          background: `linear-gradient(180deg, ${C.ink}CC 0%, ${C.ink}80 45%, ${C.ink}E6 100%)`,
        }}
      />
      {/* Bottom fade for info bar */}
      <div
        className="absolute inset-x-0 bottom-0 h-56 -z-10 pointer-events-none"
        style={{ background: `linear-gradient(180deg, transparent 0%, ${C.ink}D9 100%)` }}
      />
      {/* Cobalt accent glow */}
      <div
        className="absolute top-1/3 -left-24 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none -z-10"
        style={{ background: C.brand }}
      />

      {/* --- Content --- */}
      <div className="relative flex-1 flex flex-col max-w-[1240px] w-full mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-10">
        <div className="flex-1 flex items-center">
          <div className="max-w-[820px]">
            {/* Live tag */}
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-7 border backdrop-blur-md"
              style={{ background: "rgba(255,255,255,0.10)", borderColor: "rgba(255,255,255,0.22)" }}
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.brand, boxShadow: `0 0 12px ${C.brand}` }} />
              <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em] text-white/95">
                Live · {sc.lab_duration_label} · {sc.lab_date_primary_label}
              </span>
            </div>

            {/* Headline */}
            <h1
              data-testid="hero-headline"
              className="font-black uppercase leading-[0.92] tracking-[-0.025em] text-white"
              style={{ fontSize: "clamp(38px, 6.6vw, 88px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}
            >
              Build what you&rsquo;ve been{" "}
              <span
                style={{
                  color: "#7C8AFF",
                  textShadow: `0 0 40px ${C.brand}80`,
                }}
              >
                waiting
              </span>{" "}
              on someone else to create.
            </h1>

            {/* Supporting copy */}
            <p
              data-testid="hero-subheadline"
              className="mt-7 max-w-[620px] text-[16px] sm:text-[18px] leading-[1.55] text-white/85"
            >
              A live <strong className="text-white font-bold">{sc.lab_duration_label} online AI Build Lab</strong> where you&rsquo;ll learn to use AI and no-code tools to start building websites, apps, dashboards, workflows, and funnels &mdash; even if you&rsquo;re not technical.
            </p>

            {/* Intro-offer line */}
            {sc.intro_offer_line && (
              <p
                data-testid="hero-intro-offer"
                className="mt-4 max-w-[620px] text-[13.5px] leading-[1.55] text-white/70"
              >
                <Sparkles size={13} className="inline mr-1.5 -translate-y-px" style={{ color: "#7C8AFF" }} />
                {sc.intro_offer_line}
              </p>
            )}

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href={targetHref} data-testid="hero-primary-cta" data-cta="hero-primary">
                <Button
                  disabled={!cap.can_register}
                  className="h-14 px-7 rounded-full text-white font-bold uppercase text-[12px] sm:text-[13px] hover:opacity-95 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: C.brand,
                    letterSpacing: "0.06em",
                    boxShadow: `0 18px 40px -12px ${C.brand}CC, 0 4px 8px rgba(0,0,0,0.25)`,
                  }}
                >
                  {primaryLabel}
                  {cap.can_register && <ArrowRight size={16} className="ml-2" />}
                </Button>
              </a>
              <a href="#build" data-testid="hero-secondary-cta" data-cta="hero-secondary">
                <Button
                  variant="outline"
                  className="h-14 px-6 rounded-full font-bold uppercase text-[12px] sm:text-[13px] bg-transparent text-white hover:bg-white/10 border-2"
                  style={{ borderColor: "rgba(255,255,255,0.55)", letterSpacing: "0.06em" }}
                >
                  Show me what we&apos;ll build
                </Button>
              </a>
            </div>

            {cap.message ? (
              <p className="mt-5 text-sm text-white/70" data-testid="hero-capacity-msg">
                {cap.message}
              </p>
            ) : null}
          </div>
        </div>

        {/* Info bar — 5 stats, translucent, no tilted stickers */}
        <div className="mt-12 lg:mt-20" data-testid="hero-stats">
          <div
            className="rounded-2xl backdrop-blur-md border py-4 sm:py-5 px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5"
            style={{
              background: "rgba(10,10,10,0.55)",
              borderColor: "rgba(255,255,255,0.15)",
              boxShadow: "0 20px 50px -20px rgba(0,0,0,0.4)",
            }}
          >
            {HERO_STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`hero-stat-${i}`}>
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0 border"
                    style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.2)" }}
                  >
                    <Icon size={16} className="text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="text-[10px] font-bold uppercase tracking-[0.16em]"
                      style={{ color: "#9AA6FF" }}
                    >
                      {s.eyebrow}
                    </div>
                    <div className="text-white text-[13px] sm:text-[14px] font-bold leading-tight mt-0.5 truncate">
                      {s.get(sc)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
